import React, { useEffect, useState } from 'react';
import Modal from '../../../../components/ui/Modal';
import { Button } from '../../../../components/ui/Button';
import { WhatsAppIcon } from '../../../../components/icons/CustomIcons';

interface ConnectionSuccessModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Tempo em ms para auto-redirect (default: 5000) */
    autoRedirectMs?: number;
}

const ConnectionSuccessModal: React.FC<ConnectionSuccessModalProps> = ({
    isOpen,
    onClose,
    autoRedirectMs = 5000
}) => {
    const [countdown, setCountdown] = useState(Math.ceil(autoRedirectMs / 1000));

    // Countdown e auto-redirect
    useEffect(() => {
        if (!isOpen) {
            setCountdown(Math.ceil(autoRedirectMs / 1000));
            return;
        }

        const interval = setInterval(() => {
            setCountdown(prev => Math.max(0, prev - 1));
        }, 1000);

        return () => clearInterval(interval);
    }, [isOpen, autoRedirectMs]);

    // Triga onClose quando chega a 0
    useEffect(() => {
        if (isOpen && countdown === 0) {
            onClose();
        }
    }, [countdown, isOpen, onClose]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="sm" preventBackdropClose>
            <div className="flex flex-col items-center px-8 py-10 text-center">
                {/* Animação de confetti */}
                <div className="relative mb-6">
                    {/* Partículas de confetti */}
                    <div className="absolute -top-4 -left-6 text-2xl animate-bounce" style={{ animationDelay: '0.1s' }}>🎊</div>
                    <div className="absolute -top-6 left-2 text-xl animate-bounce" style={{ animationDelay: '0.3s' }}>🎉</div>
                    <div className="absolute -top-4 right-2 text-lg animate-bounce" style={{ animationDelay: '0.5s' }}>✨</div>
                    <div className="absolute -top-2 -right-6 text-2xl animate-bounce" style={{ animationDelay: '0.2s' }}>🎊</div>
                    <div className="absolute top-4 -left-8 text-sm animate-bounce" style={{ animationDelay: '0.4s' }}>🎀</div>
                    <div className="absolute top-4 -right-8 text-sm animate-bounce" style={{ animationDelay: '0.6s' }}>🎀</div>

                    {/* Ícone central com animação de escala */}
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center animate-[scaleIn_0.5s_ease-out]">
                        <WhatsAppIcon className="w-10 h-10 text-green-500" />
                    </div>
                </div>

                {/* Título */}
                <h2 className="text-xl font-bold text-default mb-3">
                    Dispositivo conectado!
                </h2>

                {/* Descrição */}
                <p className="text-sm text-muted leading-relaxed max-w-xs mb-6">
                    Seu dispositivo está pronto para receber e enviar mensagens.
                    Os membros do setor escolhido tem acesso a esse dispositivo,
                    você pode editar seus setores para personalizar suas permissões de acesso.
                </p>

                {/* Barra de progresso para auto-close */}
                <div className="w-full max-w-xs mb-4">
                    <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-green-500 rounded-full transition-all duration-1000 ease-linear"
                            style={{
                                width: `${(countdown / Math.ceil(autoRedirectMs / 1000)) * 100}%`
                            }}
                        />
                    </div>
                    <p className="text-xs text-muted mt-2">
                        Redirecionando em {countdown}s...
                    </p>
                </div>

                {/* Botão fechar */}
                <Button
                    variant="outline"
                    onClick={onClose}
                    className="px-8"
                >
                    Fechar
                </Button>
            </div>
        </Modal>
    );
};

export default ConnectionSuccessModal;
